"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

export type HorizontalScrollHints = {
  left: boolean;
  right: boolean;
};

export type UseHorizontalScrollRailOptions = {
  /** Re-run overflow measurement when content or layout changes */
  syncKey?: unknown;
  /** Fixed scroll distance in px; when omitted, uses first matching item width + gap */
  scrollStep?: number;
  /** CSS selector for item-based scroll step sizing */
  itemSelector?: string;
  /** Fallback item width when itemSelector finds nothing */
  defaultItemWidth?: number;
  /** External ref to attach to the scrollable rail */
  railRef?: RefObject<HTMLDivElement | null>;
  /** Outer wrapper ref; required when pinRailWidthToOuter is enabled */
  outerRef?: RefObject<HTMLDivElement | null>;
  /** Pin scroll rail width to outer bounds and enable horizontal touch drag */
  pinRailWidthToOuter?: boolean;
};

export function useHorizontalScrollRail({
  syncKey,
  scrollStep,
  itemSelector = "article",
  defaultItemWidth = 320,
  railRef: externalRailRef,
  outerRef,
  pinRailWidthToOuter = false,
}: UseHorizontalScrollRailOptions = {}) {
  const internalRailRef = useRef<HTMLDivElement | null>(null);
  const [scrollHints, setScrollHints] = useState<HorizontalScrollHints>({
    left: false,
    right: false,
  });

  const attachRailRef = useCallback((node: HTMLDivElement | null) => {
    internalRailRef.current = node;
    if (externalRailRef) {
      externalRailRef.current = node;
    }
  }, [externalRailRef]);

  const syncScrollHints = useCallback(() => {
    const rail = internalRailRef.current;
    if (!rail) return;
    const overflow = Math.max(0, rail.scrollWidth - rail.clientWidth);
    setScrollHints({
      left: rail.scrollLeft > 2,
      right: rail.scrollLeft < overflow - 2,
    });
  }, []);

  const scrollByDirection = useCallback(
    (direction: "left" | "right") => {
      const rail = internalRailRef.current;
      if (!rail) return;

      let step = scrollStep;
      if (step == null) {
        const firstItem = rail.querySelector(itemSelector);
        const gap = Number.parseFloat(getComputedStyle(rail).gap || "12") || 12;
        step =
          (firstItem?.getBoundingClientRect().width ?? defaultItemWidth) + gap;
      }

      rail.scrollBy({
        left: direction === "left" ? -step : step,
        behavior: "smooth",
      });
    },
    [defaultItemWidth, itemSelector, scrollStep],
  );

  useEffect(() => {
    const rail = internalRailRef.current;
    if (!rail) return;

    if (!pinRailWidthToOuter) {
      syncScrollHints();

      const observer = new ResizeObserver(syncScrollHints);
      observer.observe(rail);

      rail.addEventListener("scroll", syncScrollHints, { passive: true });
      return () => {
        observer.disconnect();
        rail.removeEventListener("scroll", syncScrollHints);
      };
    }

    const outer = outerRef?.current;
    if (!outer) return;

    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let axis: "x" | "y" | null = null;

    const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);

    const updateRail = () => {
      const width = Math.floor(outer.getBoundingClientRect().width);
      if (width > 0) {
        rail.style.width = `${width}px`;
        rail.style.maxWidth = `${width}px`;
      }
      syncScrollHints();
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      scrollLeft = rail.scrollLeft;
      axis = null;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (!axis) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
      if (axis !== "x" || maxScroll() <= 0) return;
      event.preventDefault();
      rail.scrollLeft = Math.max(0, Math.min(maxScroll(), scrollLeft - dx));
      updateRail();
    };

    const resetAxis = () => {
      axis = null;
    };

    updateRail();
    const observer = new ResizeObserver(updateRail);
    observer.observe(outer);
    rail.addEventListener("scroll", updateRail, { passive: true });
    window.addEventListener("resize", updateRail);
    rail.addEventListener("touchstart", onTouchStart, {
      passive: true,
      capture: true,
    });
    rail.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true,
    });
    rail.addEventListener("touchend", resetAxis, {
      passive: true,
      capture: true,
    });
    rail.addEventListener("touchcancel", resetAxis, {
      passive: true,
      capture: true,
    });

    return () => {
      observer.disconnect();
      rail.removeEventListener("scroll", updateRail);
      window.removeEventListener("resize", updateRail);
      rail.removeEventListener("touchstart", onTouchStart, true);
      rail.removeEventListener("touchmove", onTouchMove, true);
      rail.removeEventListener("touchend", resetAxis, true);
      rail.removeEventListener("touchcancel", resetAxis, true);
      rail.style.width = "";
      rail.style.maxWidth = "";
    };
  }, [outerRef, pinRailWidthToOuter, syncKey, syncScrollHints]);

  return {
    railRef: attachRailRef,
    scrollHints,
    syncScrollHints,
    scrollByDirection,
  };
}
